import { useCallback, useEffect, useRef, useState } from "react";

// ── Query<T>: "not loaded yet" is a STATE, not the absence of rows ──────────────────────────────
//
// DUPLICATED FILE — dashboard/src/lib/query.ts and portal/src/lib/query.ts must stay BYTE-IDENTICAL.
// The two apps build separately and share no package, so this is a deliberate copy rather than an
// import. "Keep them in step" is not left to memory: the `query-duplication-guard` CI job diffs the
// two files and fails the build if they drift. Edit one, copy it to the other.
//
// Every list page used to hold `const [loading, setLoading] = useState(false)` and key its empty
// state off `rows.length === 0`. The first paint happens BEFORE any effect runs, so it saw
// loading=false + rows=[] and flashed "No X match these filters" on every mount and filter change.
// Pages also folded fetch failures into the same empty list, making a backend error look identical
// to "you have no data" (that is how the Coverage 20s timeout hid as "No coverage yet").
//
// A discriminated union makes both mistakes IMPOSSIBLE TO COMPILE rather than merely discouraged:
// `data` does not exist on the loading branch, so `q.data.length === 0` will not type-check until
// you have narrowed to a state that actually has data. The builds are already tsc-gated, so this is
// enforced on every PR.
//
// "refreshing" exists so this does not regress the good behaviour it replaces: a list that already
// has rows keeps showing them (with a small Refreshing… strip) while the next request is in flight,
// instead of blanking. Without it we would trade a flash of "no results" for a flash of nothing.
export type Query<T> =
  // "idle" = nothing has been asked for yet. Real state, not a variant of empty: Voters & Universes
  // deliberately requires an explicit Search press, and "set your filters and search" must not read
  // as "no voters match". Without this the union would force pages back onto a side flag.
  | { status: "idle" }
  | { status: "loading" }
  | { status: "refreshing"; data: T }
  | { status: "error"; error: string; data?: T }
  | { status: "ready"; data: T };

/** Data if this query has any (ready or refreshing, or an error that kept its previous rows). */
export function queryData<T>(q: Query<T>): T | undefined {
  return q.status === "ready" || q.status === "refreshing" ? q.data : q.status === "error" ? q.data : undefined;
}
/** True while a request is in flight, whether or not we already have rows to show. */
export const isFetching = <T,>(q: Query<T>) => q.status === "loading" || q.status === "refreshing";

type Opts = {
  /** Debounce in ms before firing (typing filters). Default 0. */
  debounce?: number;
  /** Skip fetching entirely (e.g. no campaign selected, or a user-triggered search not yet run). */
  enabled?: boolean;
  /**
   * THE PART OF THE QUERY THAT MAKES IT A DIFFERENT QUESTION, not a narrower one.
   *
   * Holding the previous rows through a refresh is deliberate — it is what stops a filter keystroke
   * flashing an empty list. But that reasoning only holds while the old rows are APPROXIMATELY
   * RIGHT. Switching a status chip from Active to Archived is not a refinement: every row on screen
   * is now definitively wrong, and keeping them renders active records under an "Archived" heading
   * until the response lands. The chip and the list disagree, and the chip is the one telling the
   * truth.
   *
   * So: when this value changes, the held data is DROPPED and the next fetch reports "loading"
   * rather than "refreshing" — the list clears to its skeleton instead of asserting a stale answer.
   * Leave it undefined for queries where every change is a refinement (sorting, paging, typing).
   */
  resetKey?: string;
};

/**
 * Owns fetch + status + stale-response discarding so pages never hand-roll loading state.
 *
 * `key` is the identity of the request: change it and a fresh fetch is issued. Responses carry a
 * request id and a stale one is DISCARDED rather than rendered — which is what kills the
 * "filter change fires two requests and the first returns 0 rows" flash. Pages that paginate should
 * include the page in `key` and use `usePagedKey` below so the reset happens in the same pass.
 */
export function useListQuery<T>(
  key: string,
  fetcher: (signal: AbortSignal) => Promise<T>,
  opts: Opts = {},
): { query: Query<T>; reload: () => void; patch: (fn: (prev: T) => T) => void } {
  const { debounce = 0, enabled = true, resetKey } = opts;
  const [query, setQuery] = useState<Query<T>>(opts.enabled === false ? { status: "idle" } : { status: "loading" });
  const [nonce, setNonce] = useState(0);
  const reqRef = useRef(0);
  const dataRef = useRef<T | undefined>(undefined);
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;   // always call the latest closure without re-triggering the effect
  const resetRef = useRef(resetKey);

  useEffect(() => {
    if (!enabled) { setQuery({ status: "idle" }); dataRef.current = undefined; return; }
    const req = ++reqRef.current;
    const ac = new AbortController();
    // A DIFFERENT QUESTION THROWS THE OLD ANSWER AWAY. See Opts.resetKey.
    if (resetRef.current !== resetKey) { resetRef.current = resetKey; dataRef.current = undefined; }
    // Otherwise keep showing what we already have while the next request is in flight.
    setQuery(dataRef.current !== undefined
      ? { status: "refreshing", data: dataRef.current }
      : { status: "loading" });

    const run = async () => {
      try {
        const data = await fetcherRef.current(ac.signal);
        if (req !== reqRef.current) return;          // a newer request superseded this one
        dataRef.current = data;
        setQuery({ status: "ready", data });
      } catch (e: any) {
        if (req !== reqRef.current || ac.signal.aborted) return;
        // An error is its OWN state. It never becomes an empty list.
        setQuery({ status: "error", error: String(e?.message || e), data: dataRef.current });
      }
    };
    const t = debounce > 0 ? setTimeout(run, debounce) : (run(), undefined);
    return () => { if (t) clearTimeout(t); ac.abort(); };
  }, [key, nonce, enabled, debounce, resetKey]);

  /**
   * APPLY A LOCAL CHANGE WITHOUT REFETCHING — the cure for "I clicked Archive and nothing happened".
   *
   * A mutation used to be followed by reload(), which re-runs the whole fetcher. On Walkbooks that
   * is two round trips, the second of which recounts doors for every row on the page — work that
   * flipping one boolean cannot possibly have invalidated. The row you clicked showed a spinner and
   * the rest of the screen sat there looking dead for as long as the recount took.
   *
   * So: change the rows we are already holding, immediately, and let the server confirm in the
   * background. The page re-renders in the same frame as the click.
   *
   * TWO RULES FOR CALLERS, both learned the hard way elsewhere in this codebase.
   *
   * 1. YOU MUST REVERT ON FAILURE. An optimistic row that stays changed after the request failed is
   *    the UI asserting something the server never confirmed — the same lie as rendering an error as
   *    an empty list (see the Query union above), and the same rule the field app follows when it
   *    refuses to mark a knock synced without a server echo. Snapshot with queryData() before
   *    patching, restore it in the catch, and SAY SO on screen.
   * 2. Patch what you know, not what you guess. Flipping `active` is knowledge — you sent that value.
   *    Recomputing a door count locally is guessing, and guessing is what reload() is for.
   *
   * Status is preserved: patching during a refresh stays "refreshing", and patching an error state
   * keeps the error visible. This never fabricates a "ready" out of a failed load.
   */
  const patch = useCallback((fn: (prev: T) => T) => {
    if (dataRef.current === undefined) return;          // nothing loaded yet — nothing to patch
    const next = fn(dataRef.current);
    dataRef.current = next;
    setQuery((q) =>
      q.status === "refreshing" ? { status: "refreshing", data: next }
      : q.status === "error" ? { status: "error", error: q.error, data: next }
      : { status: "ready", data: next });
  }, []);

  return { query, reload: useCallback(() => setNonce((n) => n + 1), []), patch };
}

/**
 * Pagination that resets in the SAME pass as the fetch.
 *
 * A separate `useEffect(() => setPage(0), [filterKey])` fires one request with the STALE offset
 * before the corrected one — and that request can legitimately return 0 rows, painting the empty
 * state. Returning an effective page keeps the reset and the request in one render.
 */
export function usePagedKey(filterKey: string, page: number, setPage: (n: number) => void) {
  const seen = useRef(filterKey);
  const effPage = seen.current === filterKey ? page : 0;
  useEffect(() => {
    if (seen.current !== filterKey) { seen.current = filterKey; if (page !== 0) setPage(0); }
  }, [filterKey, page, setPage]);
  return effPage;
}
